import * as React from 'react';
import { useParams, Link } from 'react-router-dom';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import {
  FileText,
  ArrowLeft,
  Play,
  Square,
  RefreshCw,
  Hash,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/features/auth';
import { useContract, useContractStatusMutations } from '../hooks';
import { ChangeStatusDialog } from '../components';
import {
  formatContractCurrency,
  getContractEffectivePrice,
  ContractInfoCard,
  ContractFinancialCard,
  ContractTimelineCard,
  ContractClientCard,
  ContractSuccessPackageCard,
  ContractBillingPeriodsCard,
  ContractQuickActionsCard,
} from '../components/ContractDetailCards';
import type { ContractStatus, ContractType } from '../types';

const getStatusColor = (status: ContractStatus): { bg: string; text: string; icon: string } => {
  switch (status) {
    case 'ACTIVE':
      return { bg: 'bg-green-100', text: 'text-green-600', icon: 'text-green-600' };
    case 'NEW':
      return { bg: 'bg-blue-100', text: 'text-blue-600', icon: 'text-blue-600' };
    case 'CLOSED':
      return { bg: 'bg-slate-100', text: 'text-slate-600', icon: 'text-slate-600' };
    case 'TERMINATED':
      return { bg: 'bg-red-100', text: 'text-red-600', icon: 'text-red-600' };
    default:
      return { bg: 'bg-slate-100', text: 'text-slate-600', icon: 'text-slate-600' };
  }
};

const getStatusBadgeVariant = (
  status: ContractStatus
): 'default' | 'secondary' | 'destructive' | 'outline' => {
  switch (status) {
    case 'ACTIVE':
      return 'default';
    case 'NEW':
      return 'secondary';
    case 'CLOSED':
      return 'outline';
    case 'TERMINATED':
      return 'destructive';
    default:
      return 'outline';
  }
};

const getTypeBadgeVariant = (
  type: ContractType
): 'default' | 'secondary' | 'destructive' | 'outline' => {
  switch (type) {
    case 'HOURLY':
      return 'default';
    case 'SUCCESS_FEE':
      return 'secondary';
    case 'FIXED':
      return 'outline';
    case 'PRO_BONO':
      return 'outline';
    default:
      return 'outline';
  }
};

export function ContractDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { hasAccess } = useAuth();

  const { data: contract, isLoading, isError, refetch } = useContract(id);
  const { activateMutation, closeMutation, renewMutation } = useContractStatusMutations();

  // Dialog states
  const [statusDialogOpen, setStatusDialogOpen] = React.useState(false);
  const [statusType, setStatusType] = React.useState<'accept' | 'close' | 'new'>('accept');

  const handleStatusChange = (type: 'accept' | 'close' | 'new') => {
    setStatusType(type);
    setStatusDialogOpen(true);
  };

  const confirmStatusChange = async () => {
    if (!contract) return;

    switch (statusType) {
      case 'accept':
        await activateMutation.mutateAsync(contract.id);
        break;
      case 'close':
        await closeMutation.mutateAsync(contract.id);
        break;
      case 'new':
        await renewMutation.mutateAsync(contract.id);
        break;
    }

    setStatusDialogOpen(false);
    void refetch();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50">
        {/* Header Skeleton */}
        <div className="bg-white border-b border-slate-200 px-6 py-8">
          <div className="max-w-6xl mx-auto">
            <Skeleton className="h-4 w-24 mb-6" />
            <div className="flex items-center gap-6">
              <Skeleton className="h-16 w-16 rounded-full" />
              <div className="space-y-3">
                <Skeleton className="h-10 w-48" />
                <div className="flex gap-2">
                  <Skeleton className="h-6 w-24" />
                  <Skeleton className="h-6 w-20" />
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Content Skeleton */}
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
          </div>
        </div>
      </div>
    );
  }

  if (isError || !contract) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-lg border border-slate-200 p-8 text-center">
            <div className="text-red-500 mb-4">
              <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p className="text-lg font-medium">{t('contractNotFound')}</p>
              <p className="text-sm text-slate-500 mt-1">{t('contractNotFoundDescription')}</p>
            </div>
            <Link to="/app/contracts">
              <Button variant="outline">
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t('backToContracts')}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const canEdit = hasAccess('contract', 'update');
  const statusColors = getStatusColor(contract.status);
  const effectivePrice = getContractEffectivePrice(contract);
  const billedPrice = Number(contract.billedPrice ?? 0);
  const balance = Number(contract.balance ?? 0);
  const formattedPrice = formatContractCurrency(effectivePrice, contract.currency);
  const formattedBilledPrice = formatContractCurrency(billedPrice, contract.currency);
  const formattedBalance = formatContractCurrency(balance, contract.currency);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Header Section */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-6">
          {/* Back Button */}
          <Link
            to="/app/contracts"
            className="inline-flex items-center text-sm font-medium text-slate-600 hover:text-slate-900 mb-6 transition-colors"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('backToContracts')}
          </Link>

          {/* Contract Info */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div className="flex items-center gap-5">
              {/* Icon */}
              <div
                className={`h-16 w-16 rounded-full flex items-center justify-center border-4 border-white shadow-lg ${statusColors.bg}`}
              >
                <FileText className={`h-8 w-8 ${statusColors.icon}`} />
              </div>

              {/* Name and Badges */}
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
                  {contract.name || t('unnamedContract')}
                </h1>
                <div className="flex flex-wrap items-center gap-2">
                  {/* Contract ID Badge */}
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 border border-slate-300 text-xs font-medium uppercase tracking-wide text-slate-700">
                    <Hash className="h-3.5 w-3.5" />
                    {t('contract')} #{String(contract.id).padStart(5, '0')}
                  </span>
                  {/* Status Badge */}
                  <Badge variant={getStatusBadgeVariant(contract.status)}>
                    {t(contract.status.toLowerCase())}
                  </Badge>
                  {/* Type Badge */}
                  <Badge variant={getTypeBadgeVariant(contract.type)} className="text-xs font-medium">
                    {t(contract.type.toLowerCase())}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2">
              {contract.status === 'NEW' && canEdit && (
                <Button
                  className="bg-green-600 text-white hover:bg-green-700"
                  onClick={() => handleStatusChange('accept')}
                >
                  <Play className="mr-2 h-4 w-4" />
                  {t('activate')}
                </Button>
              )}
              {contract.status === 'ACTIVE' && canEdit && (
                <Button
                  className="bg-yellow-500 text-white hover:bg-yellow-600"
                  onClick={() => handleStatusChange('close')}
                >
                  <Square className="mr-2 h-4 w-4" />
                  {t('close')}
                </Button>
              )}
              {(contract.status === 'CLOSED' || contract.status === 'TERMINATED') && canEdit && (
                <Button
                  className="bg-blue-500 text-white hover:bg-blue-600"
                  onClick={() => handleStatusChange('new')}
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  {t('renew')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="bg-slate-50 border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex flex-wrap items-center justify-center sm:justify-start gap-6 sm:gap-8">
            <div className="text-center sm:text-left">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">
                {t('price')}
              </p>
              <p className="text-xl font-bold text-slate-900">{formattedPrice}</p>
            </div>
            <Separator orientation="vertical" className="h-10 hidden sm:block" />
            <div className="text-center sm:text-left">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">
                {t('billedPrice')}
              </p>
              <p className="text-xl font-bold text-green-600">{formattedBilledPrice}</p>
            </div>
            <Separator orientation="vertical" className="h-10 hidden sm:block" />
            <div className="text-center sm:text-left">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">
                {t('balance')}
              </p>
              <p
                className={`text-xl font-bold ${balance < 0 ? 'text-red-600' : 'text-slate-900'}`}
              >
                {formattedBalance}
              </p>
            </div>
            <Separator orientation="vertical" className="h-10 hidden sm:block" />
            <div className="text-center sm:text-left">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">
                {t('startDate')}
              </p>
              <p className="text-xl font-bold text-slate-900">
                {contract.startsAt ? format(new Date(contract.startsAt), 'MMM d, yyyy') : '-'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Info Cards Grid */}
      <div className="max-w-6xl mx-auto px-6 py-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <ContractInfoCard contract={contract} />
          <ContractFinancialCard contract={contract} />
          <ContractTimelineCard contract={contract} />
          <ContractClientCard contract={contract} />
          <ContractSuccessPackageCard contract={contract} />
          <ContractBillingPeriodsCard contract={contract} />
          <ContractQuickActionsCard contract={contract} />
        </div>
      </div>

      {/* Dialogs */}
      <ChangeStatusDialog
        open={statusDialogOpen}
        onOpenChange={setStatusDialogOpen}
        type={statusType}
        onConfirm={confirmStatusChange}
        isLoading={
          activateMutation.isPending || closeMutation.isPending || renewMutation.isPending
        }
      />
    </div>
  );
}
